import React, { useState, useEffect, FormEvent } from 'react';
import './MyProfilePage.css';
import { Link } from 'react-router-dom';
import { useAuth } from './AuthContext';

interface UserProfile {
  id: number;
  username: string;
  name: string;
  role: string;
  mobile: string | null;
  city: string | null;
  wallet_balance: number;
  sponsor_id?: string | null;
  sponsor_name?: string | null;
  account_number?: string | null;
  ifsc_code?: string | null;
  bank_name?: string | null;
  pan_number?: string | null;
}

const MyProfilePage: React.FC = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
  });
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  // State for the banking details modal
  const [isBankModalOpen, setIsBankModalOpen] = useState(false);
  const [bankDetails, setBankDetails] = useState({
    account_number: '',
    ifsc_code: '',
    bank_name: '',
    pan_number: '',
  });
  const [bankUpdateError, setBankUpdateError] = useState<string | null>(null);
  const [bankUpdateSuccess, setBankUpdateSuccess] = useState<string | null>(null);
  const [isUpdatingBank, setIsUpdatingBank] = useState(false);
  const [areTermsAccepted, setAreTermsAccepted] = useState(false);

  // Admin "View As" state
  const [viewAsUser, setViewAsUser] = useState<string>('');
  const [viewAsSearchTerm, setViewAsSearchTerm] = useState<string>('');
  const [verificationMessage, setVerificationMessage] = useState<{ status: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (user?.role === 'admin' && viewAsUser) {
          params.append('username', viewAsUser);
        }
        const response = await fetch(`/api/profile?${params.toString()}`, {
          credentials: 'include',
        });
        if (!response.ok) {
          const data = await response.json();
          setProfile(null); // Clear profile on error
          throw new Error(data.message || 'Failed to fetch profile.');
        }
        const data: UserProfile = await response.json();
        setProfile(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchProfile();
  }, [user, viewAsUser]);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsUpdating(true);
    setPasswordError(null);
    setPasswordSuccess(null);

    try {
      // If admin is viewing as another user, use the admin-specific endpoint
      const targetUserId = (user?.role === 'admin' && viewAsUser && profile) ? profile.id : user?.id;
      if (!targetUserId) {
        throw new Error("Target user ID could not be determined.");
      }
      const url = user?.role === 'admin' ? `/api/admin/users/${targetUserId}/password` : '/api/profile/password';

      const response = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password: passwordData.newPassword }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to update password.');
      }
      setPasswordSuccess(data.message);
      setPasswordData({ newPassword: '' });
      
      // Close modal after a delay to show success message
      setTimeout(() => {
        setIsPasswordModalOpen(false);
        setPasswordSuccess(null); // Clear for next time
      }, 2000);

    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'An unknown error occurred.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleOpenPasswordModal = () => {
    setPasswordError(null);
    setPasswordSuccess(null);
    setIsPasswordModalOpen(true);
  };

  const handleOpenBankModal = () => {
    if (profile) {
      setBankDetails({
        account_number: profile.account_number || '',
        ifsc_code: profile.ifsc_code || '',
        bank_name: profile.bank_name || '',
        pan_number: profile.pan_number || '',
      });
      setBankUpdateError(null);
      setBankUpdateSuccess(null);
      setAreTermsAccepted(false); // Reset terms acceptance
      setIsBankModalOpen(true);
    }
  };

  const handleBankDetailsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setBankDetails(prev => ({ ...prev, [name]: value.toUpperCase() }));
  };

  const handleBankDetailsSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsUpdatingBank(true);
    setBankUpdateError(null);
    setBankUpdateSuccess(null);

    // Validation
    if (!/^\d+$/.test(bankDetails.account_number)) {
      setBankUpdateError("Account number must only contain digits.");
      setIsUpdatingBank(false);
      return;
    }
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(bankDetails.pan_number)) {
      setBankUpdateError("Please enter a valid PAN number format (e.g., ABCDE1234F).");
      setIsUpdatingBank(false);
      return;
    }

    try {
      const targetUserId = (user?.role === 'admin' && viewAsUser && profile) ? profile.id : user?.id;
      const isAdminUpdate = user?.role === 'admin' && viewAsUser && profile;
      const url = isAdminUpdate ? `/api/admin/users/${targetUserId}/details` : '/api/profile/banking-details';

      // When an admin updates, the /details endpoint expects all user details, not just banking.
      const bodyToSend = isAdminUpdate ? {
        ...profile, // Send existing profile data
        ...bankDetails // And overwrite with the new bank details
      } : bankDetails;

      const response = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(bodyToSend),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to update details.');
      }

      setBankUpdateSuccess(data.message);
      setProfile(prev => prev ? { ...prev, ...bankDetails } : null);

      setTimeout(() => {
        setIsBankModalOpen(false);
        setBankUpdateSuccess(null);
      }, 2000);

    } catch (err) {
      setBankUpdateError(err instanceof Error ? err.message : 'An unknown error occurred.');
    } finally {
      setIsUpdatingBank(false);
    }
  };

  const handleViewAsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const usernameToView = viewAsSearchTerm.trim();
    if (!usernameToView) {
      setVerificationMessage({ status: 'error', message: 'Please enter a username to view.' });
      return;
    }
    setVerificationMessage(null);
    try {
      // We can reuse the same fetch logic to verify
      const response = await fetch(`/api/profile?username=${encodeURIComponent(usernameToView)}`, {
        credentials: 'include',
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'User not found.');
      }
      // If successful, switch the view
      setViewAsUser(usernameToView);
      setVerificationMessage({ status: 'success', message: `Now viewing profile for: ${data.name || data.username}` });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      setVerificationMessage({ status: 'error', message: errorMessage });
      setViewAsUser(''); // Clear view on error
    }
  };

  const handleClearViewAs = () => {
    setViewAsUser('');
    setViewAsSearchTerm('');
    setVerificationMessage(null);
  };

  if (isLoading) return <div className="profile-container"><h2>My Profile</h2><p>Loading profile...</p></div>;
  if (error) return <div className="profile-container"><h2>My Profile</h2><div className="error-message">{error}</div></div>;

  const areBankDetailsComplete = !!(profile && profile.account_number && profile.ifsc_code && profile.bank_name && profile.pan_number);

  return (
    <div className="profile-container">
      <style>{`
        @media (max-width: 768px) {
          .profile-grid { grid-template-columns: 1fr; }
        }
      `}</style>
      {user?.role === 'admin' && (
        <div className="admin-view-controls" style={{ backgroundColor: '#e9ecef', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
          <form onSubmit={handleViewAsSubmit} className="view-as-form" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <input type="text" value={viewAsSearchTerm} onChange={(e) => { setViewAsSearchTerm(e.target.value); setVerificationMessage(null); }} placeholder="Enter username to view profile" className="view-as-input" style={{ flexGrow: 1, padding: '10px 15px', fontSize: '1rem', borderRadius: '4px', border: '1px solid #ced4da' }} />
            <button type="submit" className="view-as-button" style={{ padding: '10px 20px', fontSize: '1rem', fontWeight: 600, cursor: 'pointer', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px' }}>View Profile</button>
            {viewAsUser && (<button type="button" onClick={handleClearViewAs} className="view-as-button clear-view-button" style={{ padding: '10px 20px', fontSize: '1rem', fontWeight: 600, cursor: 'pointer', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px' }}>View My Profile</button>)}
          </form>
          {verificationMessage && (<div className={`verification-message ${verificationMessage.status}`} style={{ marginTop: '10px', padding: '8px 12px', borderRadius: '4px', fontSize: '0.9rem', backgroundColor: verificationMessage.status === 'error' ? '#f8d7da' : '#d4edda', color: verificationMessage.status === 'error' ? '#721c24' : '#155724' }}>{verificationMessage.message}</div>)}
        </div>
      )}
      <h2>{viewAsUser && user?.role === 'admin' ? `Viewing Profile for: ${profile?.name || viewAsUser}` : 'My Profile'}</h2>
      {profile && (
        <div className="profile-details-card">
            <h3>Your Details</h3>
            <div className="profile-grid">
              <div className="detail-item"><strong>User ID:</strong><span>{profile.id}</span></div>
              <div className="detail-item"><strong>Username:</strong><span>{profile.username}</span></div>
              <div className="detail-item"><strong>Full Name:</strong><span>{profile.name}</span></div>
              <div className="detail-item"><strong>Role:</strong><span>{profile.role}</span></div>
              <div className="detail-item"><strong>Mobile:</strong><span>{profile.mobile || 'N/A'}</span></div>
              <div className="detail-item"><strong>City:</strong><span>{profile.city || 'N/A'}</span></div>
              <div className="detail-item"><strong>Sponsor ID:</strong><span>{profile.sponsor_id || 'N/A'}</span></div>
              <div className="detail-item"><strong>Sponsor Name:</strong><span>{profile.sponsor_name || 'N/A'}</span></div>
              <div className="detail-item"><strong>Wallet Balance:</strong><span>₹{profile.wallet_balance.toFixed(2)}</span></div>
            </div>
            <h4 className="bank-details-header">Bank & PAN Details</h4>
            <div className="profile-grid">
              <div className="detail-item"><strong>Account Number:</strong><span>{profile.account_number || 'N/A'}</span></div>
              <div className="detail-item"><strong>IFSC Code:</strong><span>{profile.ifsc_code || 'N/A'}</span></div>
              <div className="detail-item"><strong>Bank Name:</strong><span>{profile.bank_name || 'N/A'}</span></div>
              <div className="detail-item"><strong>PAN Number:</strong><span>{profile.pan_number || 'N/A'}</span></div>
            </div>
        </div>
      )}

      <div className="profile-actions-card">
        <h3>Account Management</h3>
        {!areBankDetailsComplete && (
          <button onClick={handleOpenBankModal} className="profile-button">
            Add Bank Details
          </button>
        )}
        <button onClick={handleOpenPasswordModal} className="profile-button">
          Change Password
        </button>
      </div>

      <div className="profile-footer-links">
        <Link to="/terms" target="_blank" rel="noopener noreferrer">Terms and Conditions</Link> | 
        <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
      </div>

      {isBankModalOpen && (
        <div className="profile-modal-overlay">
          <div className="profile-modal-content" style={{maxWidth: '500px'}}>
            <form onSubmit={handleBankDetailsSubmit}>
              <h3>Update Bank Details</h3>
              <p>Please provide your bank and PAN details to enable withdrawals.</p>
              {bankUpdateError && <div className="error-message modal-error">{bankUpdateError}</div>}
              {bankUpdateSuccess && <div className="success-message modal-success">{bankUpdateSuccess}</div>}
              <div className="form-group">
                <label htmlFor="account_number">Account Number</label>
                <input type="text" id="account_number" name="account_number" value={bankDetails.account_number} onChange={handleBankDetailsChange} required disabled={isUpdatingBank} />
              </div>
              <div className="form-group">
                <label htmlFor="ifsc_code">IFSC Code</label>
                <input type="text" id="ifsc_code" name="ifsc_code" value={bankDetails.ifsc_code} onChange={handleBankDetailsChange} required disabled={isUpdatingBank} style={{textTransform: 'uppercase'}} />
              </div>
              <div className="form-group">
                <label htmlFor="bank_name">Bank Name</label>
                <input type="text" id="bank_name" name="bank_name" value={bankDetails.bank_name} onChange={handleBankDetailsChange} required disabled={isUpdatingBank} style={{textTransform: 'uppercase'}} />
              </div>
              <div className="form-group">
                <label htmlFor="pan_number">PAN Number</label>
                <input type="text" id="pan_number" name="pan_number" value={bankDetails.pan_number} onChange={handleBankDetailsChange} required disabled={isUpdatingBank} style={{textTransform: 'uppercase'}} />
              </div>
              <div className="terms-container">
                <input
                  type="checkbox"
                  id="terms-checkbox-profile"
                  checked={areTermsAccepted}
                  onChange={(e) => setAreTermsAccepted(e.target.checked)}
                />
                <label htmlFor="terms-checkbox-profile">
                  I have read and agree to the <Link to="/terms" target="_blank" rel="noopener noreferrer">Terms and Conditions</Link> and
                  <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer"> Privacy Policy</Link>.
                </label>
              </div>
              <div className="profile-modal-actions">
                <button type="button" onClick={() => setIsBankModalOpen(false)} className="profile-button cancel-button" disabled={isUpdatingBank}>Cancel</button>
                <button type="submit" className="profile-button" disabled={isUpdatingBank || !areTermsAccepted}>
                  {isUpdatingBank ? 'Saving...' : 'Accept and Save Details'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isPasswordModalOpen && (
        <div className="profile-modal-overlay">
          <div className="profile-modal-content">
            <form onSubmit={handlePasswordSubmit}>
              <h3>Change Your Password</h3>
              {passwordError && <div className="error-message modal-error">{passwordError}</div>}
              {passwordSuccess && <div className="success-message modal-success">{passwordSuccess}</div>}
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="text"
                  id="newPassword"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  required
                  disabled={isUpdating}
                  autoFocus
                />
              </div>
              <div className="profile-modal-actions">
                <button type="button" onClick={() => setIsPasswordModalOpen(false)} className="profile-button cancel-button" disabled={isUpdating}>Cancel</button>
                <button type="submit" className="profile-button" disabled={isUpdating}>
                  {isUpdating ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyProfilePage;